import React, { useState, useEffect } from 'react';
 import { TrendingUp, PieChart as PieChartIcon, BarChart3, Calendar } from 'lucide-react';
import CategorySelect from './CategorySelect';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiClient } from '../api/client';
import { formatINR } from '../utils/currency';

interface ExpenseAnalyticsProps {
  className?: string;
}

interface TrendData {
  period: string;
  amount: string;
  average: string;
}

interface CategoryBreakdown {
  category_id: number;
  category_name: string;
  amount: string;
  percentage: number;
}

interface TopCategory {
  category_id: number;
  category_name: string;
  category_path: string;
  amount: string;
  percentage: number;
}

const COLORS = [
  '#c09a6f', // gold
  '#6366f1', // indigo
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#f59e0b', // amber
  '#10b981', // emerald
  '#06b6d4', // cyan
  '#ef4444', // red
  '#f97316', // orange
  '#84cc16', // lime
];

const ExpenseAnalytics: React.FC<ExpenseAnalyticsProps> = ({ className = "" }) => {
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [categoryData, setCategoryData] = useState<CategoryBreakdown[]>([]);
  const [topCategories, setTopCategories] = useState<TopCategory[]>([]);
  const [totalSpending, setTotalSpending] = useState<string>('0');
  const [averagePerPeriod, setAveragePerPeriod] = useState<string>('0');
  
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    category_id: undefined as number | undefined,
    group_by: 'day',
  });

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      const [trendsData, categoryBreakdown, topCats] = await Promise.all([
        apiClient.getSpendingTrends({
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          category_id: filters.category_id,
          group_by: filters.group_by,
        }),
        apiClient.getSpendingByCategory({
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          category_id: filters.category_id,
          include_descendants: true,
          include_subcategories: true,
        }),
        apiClient.getTopCategories({
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          category_id: filters.category_id,
          include_descendants: true,
          limit: 10,
        }),
      ]);

      setTrends(trendsData);
      setCategoryData(categoryBreakdown.categories || []);
      setTopCategories(topCats);
      
      // Calculate total and average from trends data (which respects all filters)
      if (trendsData.length > 0) {
        // Calculate total from all periods
        const total = trendsData.reduce((sum, item) => sum + parseFloat(item.amount), 0);
        setTotalSpending(total.toString());
        
        // Calculate number of periods in the date range
        let periodCount = trendsData.length;
        
        if (filters.start_date && filters.end_date) {
          const startDate = new Date(filters.start_date);
          const endDate = new Date(filters.end_date);
          const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
          
          if (filters.group_by === 'month') {
            // Calculate months between dates
            const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
                          (endDate.getMonth() - startDate.getMonth()) + 1;
            periodCount = months;
          } else if (filters.group_by === 'week') {
            // Calculate weeks between dates
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            periodCount = Math.ceil(diffDays / 7);
          } else {
            // Calculate days between dates
            periodCount = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
          }
        }
        
        // Calculate average per period (total / all periods in range)
        const avg = total / periodCount;
        setAveragePerPeriod(avg.toString());
      } else {
        setTotalSpending('0');
        setAveragePerPeriod('0');
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string | number | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      category_id: undefined,
      group_by: 'day',
    });
  };

  // Format trend data for the chart with average
  const formattedTrends = trends.map(item => ({
    period: item.period,
    total: parseFloat(item.amount),
    average: parseFloat(item.average),
  }));

  // Format category data for pie chart
  const formattedCategoryData = categoryData.map((item, index) => ({
    name: item.category_name,
    value: parseFloat(item.amount),
    percentage: item.percentage,
    color: COLORS[index % COLORS.length],
  }));

  if (loading) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="text-slate-400">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header and Filters */}
      <div className="bg-white/5 rounded-lg border border-white/10 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">Expense Analytics</h3>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <p className="text-slate-400">
                Total Spending: <span className="text-green-400 font-medium">{formatINR(totalSpending)}</span>
              </p>
              <p className="text-slate-400">
                Avg per Period: <span className="text-indigo-400 font-medium">{formatINR(averagePerPeriod)}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-[var(--accent-gold)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-[var(--accent-gold)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Category
            </label>
            <CategorySelect
              value={filters.category_id}
              onChange={(categoryId) => handleFilterChange('category_id', categoryId || undefined)}
              placeholder="All categories"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Group By
            </label>
            <select
              value={filters.group_by}
              onChange={(e) => handleFilterChange('group_by', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-[var(--accent-gold)]"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
        </div>

        {(filters.start_date || filters.end_date || filters.category_id) && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-[var(--accent-gold)] hover:bg-[#d4b896] text-white rounded-md transition-colors text-sm"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Spending Trends Chart */}
      <div className="bg-white/5 rounded-lg border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-[var(--accent-gold)]" />
          <h4 className="text-lg font-semibold text-white">Spending Trends</h4>
        </div>
        
        {formattedTrends.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={formattedTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
              <XAxis
                dataKey="period"
                stroke="#94a3b8"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#94a3b8"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `₹${value.toLocaleString()}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                }}
                formatter={(value: number) => [`₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Amount']}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#c09a6f"
                strokeWidth={2}
                dot={{ fill: '#c09a6f', r: 4 }}
                activeDot={{ r: 6 }}
                name="Spending"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-slate-400">
            No spending data available for the selected period
          </div>
        )}
      </div>

      {/* Category Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div className="bg-white/5 rounded-lg border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <PieChartIcon className="h-5 w-5 text-[var(--accent-gold)]" />
            <h4 className="text-lg font-semibold text-white">
              {filters.category_id ? 'Subcategory Distribution' : 'Category Distribution'}
            </h4>
          </div>
          
          {formattedCategoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={formattedCategoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name} (${percentage}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {formattedCategoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                  formatter={(value: number) => `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : filters.category_id ? (
            <div className="text-center py-8 text-slate-400">
              No subcategories found for the selected category
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              No category data available
            </div>
          )}
        </div>

        {/* Bar Chart */}
        <div className="bg-white/5 rounded-lg border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5 text-[var(--accent-gold)]" />
            <h4 className="text-lg font-semibold text-white">
              {filters.category_id ? 'Subcategory Comparison' : 'Category Comparison'}
            </h4>
          </div>
          
          {formattedCategoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={formattedCategoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                <XAxis 
                  dataKey="name" 
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `₹${value.toLocaleString()}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                  formatter={(value: number) => `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                />
                <Bar dataKey="value" name="Amount">
                  {formattedCategoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : filters.category_id ? (
            <div className="text-center py-8 text-slate-400">
              No subcategories found for the selected category
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              No category data available
            </div>
          )}
        </div>
      </div>

      {/* Top Spending Categories */}
      <div className="bg-white/5 rounded-lg border border-white/10 p-6">
        <h4 className="text-lg font-semibold text-white mb-4">
          {filters.category_id ? 'Top Spending Subcategories' : 'Top Spending Categories'}
        </h4>
        
        {topCategories.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topCategories.map((category, index) => (
              <div
                key={category.category_id}
                className="bg-white/5 rounded-lg border border-white/10 p-4 hover:border-[var(--accent-gold)]/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-300 mb-1">
                      {category.category_path}
                    </div>
                    <div className="text-lg font-bold text-white">
                      {formatINR(category.amount)}
                    </div>
                  </div>
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  >
                    {index + 1}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white/10 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${category.percentage}%`,
                        backgroundColor: COLORS[index % COLORS.length],
                      }}
                    />
                  </div>
                  <span className="text-sm text-slate-400">{category.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        ) : filters.category_id ? (
          <div className="text-center py-8 text-slate-400">
            No subcategories found for the selected category
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            No spending data available
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseAnalytics;